import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Search,
  Eye,
  ScrollText,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { adminVendorDocumentsApi } from '../../services/adminVendorDocumentsService';
import { AdminPageHeader, AdminGuideCallout, AdminEmptyState } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';
import { formatDate } from '../../utils/dateUtils';

const statusChip = (status) => {
  switch (String(status || '').toLowerCase()) {
    case 'signed':
      return 'bg-emerald-50 text-emerald-800 border-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/50';
    case 'sent':
      return 'bg-sky-50 text-sky-800 border-sky-200/80 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800/50';
    case 'draft':
      return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600';
    case 'expired':
    case 'cancelled':
      return 'bg-red-50 text-red-800 border-red-200/80 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800/50';
    default:
      return 'bg-amber-50 text-amber-800 border-amber-200/80 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800/50';
  }
};

const STATUS_PILLS = [
  { value: '', label: 'All' },
  { value: 'signed', label: 'Signed' },
  { value: 'sent', label: 'Sent' },
  { value: 'draft', label: 'Draft' },
];

const AdminVendorContracts = () => {
  const { id } = useParams();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-vendor-contracts', id, page, search, status],
    queryFn: () =>
      adminVendorDocumentsApi.getContracts(id, {
        page,
        per_page: 15,
        search: search || undefined,
        status: status || undefined,
      }),
    enabled: !!id,
    keepPreviousData: true,
  });

  const vendor = data?.vendor;
  const contracts = data?.data || [];
  const meta = data?.meta || {};
  const businessName = vendor?.business_name || vendor?.name || 'Vendor';

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Vendor contracts"
        subtitle={`${businessName} — agreements sent for client signature.`}
        actions={
          <Link
            to={`/admin/vendors/${id}`}
            className="inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 px-3.5 py-2 text-sm font-medium !rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" />
            Back to vendor
          </Link>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.vendorContracts.title}
        steps={ADMIN_PAGE_GUIDES.vendorContracts.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5 space-y-4">
        <div
          className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
          role="tablist"
        >
          {STATUS_PILLS.map((pill) => {
            const active = status === pill.value;
            return (
              <button
                key={pill.value || 'all'}
                type="button"
                onClick={() => {
                  setStatus(pill.value);
                  setPage(1);
                }}
                className={`px-3 py-1.5 text-sm font-medium !rounded-lg transition-colors ${
                  active
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-gray-50'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400'
                }`}
              >
                {pill.label}
              </button>
            );
          })}
        </div>

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search contract #, title, client…"
            className="w-full pl-10 pr-3 py-2.5 text-sm !rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 py-16 flex items-center justify-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
          Loading contracts…
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          Could not load contracts.
        </div>
      ) : contracts.length === 0 ? (
        <AdminEmptyState
          icon={ScrollText}
          title="No contracts yet"
          description="This vendor has not created any contracts that match your filters."
        />
      ) : (
        <div className="space-y-3">
          {contracts.map((c) => (
            <Link
              key={c.id}
              to={`/admin/vendors/${id}/contracts/${c.id}`}
              className="block rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 p-4 sm:p-5 shadow-sm hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
            >
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                  {c.title || c.contract_number}
                </h3>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${statusChip(
                    c.status
                  )}`}
                >
                  {c.status || 'unknown'}
                </span>
                {c.is_expired && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200/80">
                    Expired
                  </span>
                )}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-0.5">
                  <p>
                    <span className="text-gray-500">#</span> {c.contract_number || '—'}
                    {' · '}
                    <span className="text-gray-500">Client:</span> {c.client_name || '—'}
                  </p>
                  <p>
                    Created {c.created_at ? formatDate(c.created_at, 'short') : '—'}
                    {c.signed_at ? ` · Signed ${formatDate(c.signed_at, 'short')}` : ''}
                    {c.booking?.booking_reference
                      ? ` · Booking ${c.booking.booking_reference}`
                      : ''}
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 dark:text-primary-300">
                  <Eye className="w-3.5 h-3.5" />
                  View
                </span>
              </div>
            </Link>
          ))}

          {meta.last_page > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-gray-500">
                Page {meta.current_page} of {meta.last_page}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 text-xs font-medium !rounded-xl border border-gray-200 dark:border-gray-600 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= meta.last_page}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-xs font-medium !rounded-xl border border-gray-200 dark:border-gray-600 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminVendorContracts;
